'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';
import { useRef } from 'react';

import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Breadcrumbs } from '../homepage/breadcrumbs';
import { Paragraph } from '../ui/paragraph';
import { ButtonIcon } from '../ui/button-icon';
import { buildCategoryBreadcrumb } from '@/lib/category-breadcrumb';

export function CategoryChildlessHeader({ category }: { category: Category }) {
  const breadcrumb = buildCategoryBreadcrumb(category);
  const sectionRef = useRef<HTMLElement>(null);

  const handleScrollPastSection = () => {
    if (!sectionRef.current) return;
    const sectionHeight = sectionRef.current.getBoundingClientRect().height;
    window.scrollTo({ top: sectionHeight, behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      className={cn(
        'custom-section',
        'pb-6 pt-12 sm:pb-8 sm:pt-16 md:pb-10 md:pt-20 lg:pb-12 lg:pt-24',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-3 sm:gap-8 md:flex-row md:items-stretch md:justify-between lg:gap-12',
        )}
      >
        <div className="flex w-full max-w-[688px] flex-col items-start justify-between gap-8">
          <div className="flex w-full flex-col items-start justify-start gap-3 lg:gap-4">
            <Breadcrumbs breadcrumb={breadcrumb} />
            <h1
              className={cn(
                'h1',
                'w-full text-center font-semibold tracking-tight text-black sm:text-left',
              )}
            >
              {category.name}
            </h1>
            {!!category?.description && (
              <Paragraph
                className="w-full text-center sm:text-left"
                innerHtml={category?.description}
                color="muted-foreground"
                size="lg"
              />
            )}
          </div>
          <div
            role="button"
            onClick={handleScrollPastSection}
            className={cn(
              'generic-hover',
              'hidden cursor-pointer items-center justify-start text-sm font-semibold tracking-tight text-primary md:flex lg:text-base',
            )}
          >
            <ButtonIcon
              alt="Nakupovať"
              url="/images/icons/arrow-down-2-primary.svg"
              className="lg:max-h-6 lg:min-h-6 lg:min-w-6 lg:max-w-6"
            />
            Nakupovať
          </div>
        </div>
        {!!category?.image && (
          <Image
            src={category?.image?.url}
            alt={resolveImageAlt(category?.image, category?.name)}
            width={0}
            height={0}
            className="h-[196px] w-full sm:h-[240px] sm:max-w-[569px] lg:h-[320px]"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        )}
      </div>
    </section>
  );
}
